import React, {useState} from 'react'
import moment from 'moment'
import styled from 'styled-components'
import {
  Box,
  Card as MuiCard,
  CardContent,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'

import {
  NumberField,
  PaginatedTable,
  RelatedLinkedField,
  useQueryPaginator,
  ValueField,
} from 'src/components/Table'
import {DamagedListTable} from 'src/components/Table/list/Damaged'
import {ASNUnitListTable} from 'src/components/Table/list/ASNUnit'
import {ASNExpectedUnitListTable} from 'src/components/Table/list/ASNExpectedUnit'
import {skuit} from 'src/services/skuit'

const Card = styled(MuiCard)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface TabProps {
  start: moment.Moment
  end: moment.Moment
}

const SummaryTab = (props: TabProps) => {
  const { start, end } = props
  const exportFilters = {start: start.toISOString(), end: end.toISOString()}

  const DownloadManagerMap: any = {
    overage: skuit.client.asn.overages.exportSummary,
    shortage: skuit.client.asn.shortages.exportSummary,
    damage: skuit.client.damaged.exportSummary,
  }

  const FieldsMap: any = {
    overage: ['sku', 'overage'],
    shortage: ['sku', 'shortage'],
    damage: ['sku', 'num_units'],
  }

  const overagesSummaryPaginator = useQueryPaginator(
    'overages-summary-list',
    skuit.client.asn.overages.summary,
    {params: {page_size: 5, received_on_start: start.toISOString(), received_on_end: end.toISOString()}}
  )
  const shortagesSummaryPaginator = useQueryPaginator(
    'shortages-summary-list',
    skuit.client.asn.shortages.summary,
    {params: {page_size: 5, received_on_start: start.toISOString(), received_on_end: end.toISOString()}}
  )
  const damageSummaryPaginator = useQueryPaginator(
    'damage-summary-list',
    skuit.client.damaged.summary,
    {params: {page_size: 5, damaged_on_start: start.toISOString(), damaged_on_end: end.toISOString()}}
  )

  return (
    <>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <PaginatedTable
            title="Over Summary"
            fields={[
              {id: 'sku', label: 'Number', renderer: RelatedLinkedField(ValueField, 'sku')},
              {id: 'overage', label: 'Over', renderer: NumberField},
            ]}
            paginator={overagesSummaryPaginator}
            exportConfig={{
              exportFields: FieldsMap.overage,
              exportAPI: DownloadManagerMap.overage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Short Summary"
            fields={[
              {id: 'sku', label: 'Number', renderer: RelatedLinkedField(ValueField, 'sku')},
              {id: 'shortage', label: 'Short', renderer: NumberField},
            ]}
            paginator={shortagesSummaryPaginator}
            exportConfig={{
              exportFields: FieldsMap.shortage,
              exportAPI: DownloadManagerMap.shortage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Damages Summary"
            fields={[
              {id: 'sku', label: 'Number', renderer: RelatedLinkedField(ValueField, 'sku')},
              {id: 'numUnits', label: 'Number of Units', ordering: 'num_units', renderer: NumberField},
            ]}
            paginator={damageSummaryPaginator}
            exportConfig={{
              exportFields: FieldsMap.damage,
              exportAPI: DownloadManagerMap.damage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

const DetailsTab = (props: TabProps) => {
  const { start, end } = props
  const exportFilters = {start: start.toISOString(), end: end.toISOString()}

  const DownloadManagerMap: any = {
    overage: skuit.client.asn.overages.export,
    shortage: skuit.client.asn.shortages.export,
    damage: skuit.client.damaged.export,
  }

  const FieldsMap: any = {
    overage: ['asn_guid',
        'asn_name',
        'overage',
        'product_guid',
        'product_name',
        'received_on',
        'sku_guid',
        'sku'],
    shortage: ['asn_guid',
        'asn_name',
        'product_guid',
        'product_name',
        'received_on',
        'shortage',
        'sku_guid',
        'sku'],
    damage: ['guid',
        'facility',
        'asn',
        'product',
        'sku',
        'damage',
        'by_us',
        'notes',
        'damaged_on'],
  }

  const overagesPaginator = useQueryPaginator(
    'overages-list',
    skuit.client.asn.overages.list,
    {params: {page_size: 5, received_on_start: start.toISOString(), received_on_end: end.toISOString()}}
  )
  const shortagesPaginator = useQueryPaginator(
    'shortages-list',
    skuit.client.asn.shortages.list,
    {params: {page_size: 5, received_on_start: start.toISOString(), received_on_end: end.toISOString()}}
  )
  const damagesPaginator = useQueryPaginator(
    'damage-list',
    skuit.client.damaged.list,
    {params: {page_size: 5, damaged_on_start: start.toISOString(), damaged_on_end: end.toISOString()}}
  )

  return (
    <>   
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <ASNUnitListTable
            paginator={overagesPaginator}
            title="Over"
            fields={['asn', 'sku', 'product', 'overage']}
            exportConfig={{
              exportFields: FieldsMap.overage,
              exportAPI: DownloadManagerMap.overage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <ASNExpectedUnitListTable
            paginator={shortagesPaginator}
            title="Short"
            fields={['asn', 'sku', 'product', 'shortage']}
            exportConfig={{
              exportFields: FieldsMap.shortage,
              exportAPI: DownloadManagerMap.shortage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <DamagedListTable
            paginator={damagesPaginator}
            title="Damaged"
            fields={['guid', 'sku', 'damage', 'damagedOn']}
            exportConfig={{
              exportFields: FieldsMap.damage,
              exportAPI: DownloadManagerMap.damage,
              exportFilters: exportFilters,
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const TabPanel = (props: TabPanelProps) => {
  const {children = [], value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const DashboardOSandDPage = () => {
  const [tab, setTab] = useState(0)
  const [start, setStart] = useState(moment().subtract(1, 'days'))
  const [end, setEnd] = useState(moment().subtract(1, 'days'))

  start.set({hour: 0, minute: 0, second: 0})
  end.set({hour: 23, minute: 59, second: 59})

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  return (
    <>
      <Card mb={6}>
        <CardContent>
          <Centered>
            <Typography>
              Date Range
            </Typography>
            <Grid container spacing={6}>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  id="start"
                  name="Start"
                  variant="standard"
                  label="Start"
                  fullWidth
                  inputProps={{
                    max: end.format('YYYY-MM-DD'),
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setStart(moment(e.target.value))
                  }}
                  value={start.format('YYYY-MM-DD')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  id="start"
                  name="End"
                  variant="standard"
                  label="End"
                  fullWidth
                  inputProps={{
                    min: start.format('YYYY-MM-DD'),
                    max: moment().format('YYYY-MM-DD'),
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEnd(moment(e.target.value))
                  }}
                  value={end.format('YYYY-MM-DD')}
                />
              </Grid>
            </Grid>
          </Centered>
        </CardContent>
      </Card>
      <Tabs value={tab} onChange={handleTabChange} aria-label="tabs">
        <Tab label="Summary" />
        <Tab label="Details" />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <SummaryTab start={start} end={end} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <DetailsTab start={start} end={end} />
      </TabPanel>
    </>
  )
}
